import React, { useState } from 'react';
import { MapPin, Phone, Smartphone, Mail, Send, ArrowUpRight } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { useToast } from '../hooks/use-toast';
import { useSite } from '../context/SiteContext';
import { WhatsAppInlineButton } from './WhatsAppWidget';

const Contact = () => {
  const { contact } = useSite();
  const { toast } = useToast();
  const [form, setForm] = useState({ name: '', email: '', subject: '', message: '' });
  const [submitting, setSubmitting] = useState(false);

  const onChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const onSubmit = (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast({ title: 'Missing details', description: 'Please fill in name, email, and message.' });
      return;
    }
    setSubmitting(true);
    try {
      const key = 'recreasol_inquiries';
      const existing = JSON.parse(localStorage.getItem(key) || '[]');
      existing.push({ ...form, at: new Date().toISOString() });
      localStorage.setItem(key, JSON.stringify(existing));
    } catch (err) {
      /* ignore */
    }
    setTimeout(() => {
      setSubmitting(false);
      setForm({ name: '', email: '', subject: '', message: '' });
      toast({
        title: 'Inquiry sent',
        description: "Thanks! We\u2019ll get back to you shortly.",
      });
    }, 700);
  };

  return (
    <section id="contact" className="section">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5">
            <p className="text-xs uppercase tracking-[0.24em] text-brand-green-light">{contact.eyebrow}</p>
            <h2 className="mt-3 text-3xl md:text-4xl font-bold text-white leading-tight">
              {contact.heading}
            </h2>
            <p className="mt-4 text-slate-400">{contact.body}</p>

            <div className="mt-8 space-y-4">
              <InfoRow icon={MapPin} label="Head office">
                <div>{contact.company}</div>
                <div className="text-slate-400">{contact.address}</div>
                <a
                  href={contact.mapUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-1 text-brand-green-light text-sm mt-1 hover:text-white btn-transition"
                >
                  View on Google Maps <ArrowUpRight className="h-3.5 w-3.5" />
                </a>
              </InfoRow>
              <InfoRow icon={Phone} label="Telephone">
                <a href={`tel:${contact.phone.replace(/\s/g, '')}`} className="hover:text-brand-green-light btn-transition">
                  {contact.phone}
                </a>
              </InfoRow>
              <InfoRow icon={Smartphone} label="Mobile">
                <a href={`tel:${contact.mobile.replace(/\s/g, '')}`} className="hover:text-brand-green-light btn-transition">
                  {contact.mobile}
                </a>
              </InfoRow>
              <InfoRow icon={Mail} label="Email">
                <a href={`mailto:${contact.email}`} className="hover:text-brand-green-light btn-transition">
                  {contact.email}
                </a>
              </InfoRow>
            </div>
          </div>

          <div className="lg:col-span-7">
            <form
              onSubmit={onSubmit}
              className="rounded-2xl p-6 md:p-8 border border-white/8 bg-white/[0.02] relative overflow-hidden"
            >
              <div
                aria-hidden
                className="absolute -top-24 -right-24 h-56 w-56 rounded-full bg-brand-green/10 blur-3xl"
              />
              <div className="relative grid md:grid-cols-2 gap-5">
                <Field label="Your Name" required>
                  <Input
                    name="name"
                    value={form.name}
                    onChange={onChange}
                    placeholder="John Doe"
                    className="bg-black/30 border-white/10 text-white placeholder:text-slate-500 focus-visible:ring-brand-green/60"
                  />
                </Field>
                <Field label="Your Email" required>
                  <Input
                    name="email"
                    type="email"
                    value={form.email}
                    onChange={onChange}
                    placeholder="you@company.com"
                    className="bg-black/30 border-white/10 text-white placeholder:text-slate-500 focus-visible:ring-brand-green/60"
                  />
                </Field>
                <div className="md:col-span-2">
                  <Field label="Subject">
                    <Input
                      name="subject"
                      value={form.subject}
                      onChange={onChange}
                      placeholder="POS deployment inquiry"
                      className="bg-black/30 border-white/10 text-white placeholder:text-slate-500 focus-visible:ring-brand-green/60"
                    />
                  </Field>
                </div>
                <div className="md:col-span-2">
                  <Field label="Your Message" required>
                    <Textarea
                      name="message"
                      value={form.message}
                      onChange={onChange}
                      rows={5}
                      placeholder="Tell us about your project or requirement…"
                      className="bg-black/30 border-white/10 text-white placeholder:text-slate-500 focus-visible:ring-brand-green/60 resize-none"
                    />
                  </Field>
                </div>
              </div>
              <div className="relative mt-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
                <p className="text-xs text-slate-500">By submitting, you agree to be contacted about your inquiry.</p>
                <div className="flex flex-wrap items-center gap-2">
                  <WhatsAppInlineButton />
                  <Button
                    type="submit"
                    disabled={submitting}
                    className="bg-brand-green hover:bg-brand-green-light text-black font-medium h-11 px-5 btn-transition flex items-center gap-2"
                  >
                    {submitting ? 'Sending…' : (<>Send inquiry <Send className="h-4 w-4" /></>)}
                  </Button>
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

const InfoRow = ({ icon: Icon, label, children }) => (
  <div className="flex items-start gap-3">
    <div className="h-10 w-10 rounded-lg bg-brand-green/10 border border-brand-green/30 flex items-center justify-center flex-shrink-0">
      <Icon className="h-4 w-4 text-brand-green-light" />
    </div>
    <div>
      <div className="text-[11px] uppercase tracking-widest text-slate-500">{label}</div>
      <div className="mt-0.5 text-white text-sm">{children}</div>
    </div>
  </div>
);

const Field = ({ label, required, children }) => (
  <div>
    <Label className="text-xs uppercase tracking-widest text-slate-400">
      {label} {required && <span className="text-brand-pink-light">*</span>}
    </Label>
    <div className="mt-2">{children}</div>
  </div>
);

export default Contact;
